import React, { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";
import { ArrowUpRight } from "lucide-react";
import projects from "../../data/projects";
import { getFeaturedProjects } from "../../lib/projectHelpers";
import { trackEvent } from "../../lib/track";

const FEATURED = getFeaturedProjects(projects);
// Duplicate the list so the CSS marquee can loop seamlessly via
// translateX(0 → -50%). Keys are suffixed with the half index so
// React keeps both halves stable across re-renders.
const DRIFT_LIST = FEATURED.length > 0 ? [...FEATURED, ...FEATURED] : [];

// Motion speed presets — `duration` is the full loop time of the
// translate animation (lower = faster drift). Pause stops the animation
// without resetting position so the same card stays visible.
const SPEEDS = [
  { id: "slow", label: "Slow", duration: 90 },
  { id: "normal", label: "Normal", duration: 60 },
  { id: "fast", label: "Fast", duration: 32 },
  { id: "pause", label: "Pause", duration: null },
];

/**
 * Momentum in Motion · Showcase Teaser (homepage).
 *
 * Replaces the static 3-card teaser with a continuous cinematic drift
 * of every featured project. Visitors get motion as the affordance:
 * cards slide right-to-left at a configurable cadence, pausing on
 * hover or focus. A minimalist text-only "Motion Control" pill row
 * lets the visitor pick Slow / Normal / Fast / Pause. All clicks
 * still route to /proof so the funnel intent is preserved.
 *
 * Copy below ladders the visitor through:
 *   1. What this section IS — clarity in action.
 *   2. The bridge — these started from the same Clarity Reset Session
 *      that is available to them.
 *   3. Dual CTAs — primary path forward (Reset) + secondary (See proof).
 */
const ShowcaseTeaser = () => {
  const [speedId, setSpeedId] = useState("normal");
  const trackRef = useRef(null);

  // Visitors who prefer reduced motion get a static rail from the
  // start. The CSS rule already handles the visual, this just sets
  // the active state in the control pills so it matches reality.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) setSpeedId("pause");
  }, []);

  const active = SPEEDS.find((s) => s.id === speedId) || SPEEDS[1];
  // When the speed changes, we re-mount the track so the browser
  // resets the CSS animation cleanly. Without this, mutating
  // animation-duration mid-flight makes some browsers stall or
  // freeze the marquee. Re-mounting forces a fresh animation
  // lifecycle and keeps motion fluid across speed changes.
  const trackKey = `momentum-drift-${speedId}`;

  const openOrientationReset = () => {
    trackEvent("momentum_motion_reset_cta_clicked", { surface: "showcase_teaser" });
    window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
  };

  if (FEATURED.length === 0) return null;

  return (
    <section
      id="showcase-teaser"
      data-testid="showcase-teaser-section"
      className="relative py-24 md:py-32 border-t border-gold/10"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
            Momentum in Motion · Proof of the Path
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-themed font-light text-center max-w-3xl mx-auto">
            What clarity{" "}
            <span className="italic gold-gradient-text">can become.</span>
          </h2>
          <p
            data-testid="showcase-teaser-copy"
            className="mt-8 text-themed-muted leading-relaxed max-w-2xl mx-auto text-center"
          >
            Momentum in Motion shows what clarity can become when it is turned into action. These projects are examples of ideas, brands, and systems becoming more structured, visible, and usable.
          </p>
        </Reveal>

        {/* ─── Drift carousel ──────────────────────────────────── */}
        <Reveal delay={140}>
          <div
            className="mt-14 relative momentum-drift"
            data-testid="showcase-teaser-drift"
            aria-roledescription="carousel"
            aria-label="Featured projects, drifting"
          >
            <div className="momentum-drift-wrap">
              <div
                data-testid="showcase-teaser-drift-fade"
                className="momentum-drift-fade-in"
              >
                <div
                  key={trackKey}
                  ref={trackRef}
                  data-testid="showcase-teaser-drift-track"
                  className={`momentum-drift-track ${speedId === "pause" ? "is-paused" : ""}`}
                  style={{
                    // Drive the keyframe via a CSS custom property
                    // (`--am-drift-duration`) so the stylesheet rule
                    // can pick it up via `animation-duration: var(...)`.
                    // For PAUSE, we keep the previous duration in place
                    // and rely solely on the `.is-paused` class which
                    // applies `animation-play-state: paused !important`.
                    "--am-drift-duration": active.duration
                      ? `${active.duration}s`
                      : "60s",
                  }}
                >
                {DRIFT_LIST.map((p, i) => (
                  <a
                    key={`${p.id || p.name}-${i}`}
                    href="/proof"
                    data-testid={`showcase-teaser-card-${i}`}
                    aria-hidden={i >= FEATURED.length}
                    onClick={() =>
                      trackEvent("showcase_teaser_clicked", { project: p.name })
                    }
                    className="momentum-drift-card glass-card block group overflow-hidden relative focus:outline-none focus:ring-1 focus:ring-gold/60"
                  >
                    <ArrowUpRight
                      size={16}
                      strokeWidth={1.4}
                      className="absolute top-5 right-5 text-gold/50 group-hover:text-gold transition-colors z-10"
                      aria-hidden
                    />
                    {p.thumb ? (
                      <div className="aspect-[4/3] overflow-hidden bg-black/30">
                        <img
                          src={p.thumb}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        />
                      </div>
                    ) : null}
                    <div className="p-6">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-2">
                        {p.category}
                      </div>
                      <div className="font-serif text-lg text-themed group-hover:text-gold transition-colors">
                        {p.name}
                      </div>
                      {p.brief ? (
                        <p className="mt-2 text-sm text-themed-muted leading-relaxed line-clamp-2">
                          {p.brief}
                        </p>
                      ) : null}
                    </div>
                  </a>
                ))}
                </div>
              </div>
            </div>

            {/* Motion control — sibling of `.momentum-drift`, NOT a
                child. Critical: keeping these buttons inside the drift
                container would trip `.momentum-drift:focus-within` on
                button click and pause the carousel until focus blurs.
                Living here, the buttons can take focus freely and the
                carousel keeps moving. */}
          </div>
            <div
              data-testid="momentum-motion-control"
              role="group"
              aria-label="Motion speed"
              className="momentum-motion-control mt-8 flex items-center justify-center gap-1 sm:gap-2"
            >
              <span className="text-[10px] tracking-[0.4em] uppercase text-themed-muted/60 mr-2 sm:mr-4 hidden sm:inline">
                Motion
              </span>
              {SPEEDS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`momentum-motion-${s.id}`}
                  onClick={(e) => {
                    setSpeedId(s.id);
                    trackEvent("momentum_motion_speed_changed", { speed: s.id });
                    // Drop focus immediately so even ambient focus
                    // styling (e.g. browser focus ring) cannot keep
                    // any ancestor in a focus-within state long
                    // enough to stall the marquee.
                    e.currentTarget.blur();
                  }}
                  aria-pressed={speedId === s.id}
                  className={`momentum-motion-btn ${speedId === s.id ? "is-active" : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
        </Reveal>

        {/* ─── Strategic bridge ─────────────────────────────────── */}
        <Reveal delay={300}>
          <p
            data-testid="showcase-teaser-bridge"
            className="mt-16 max-w-2xl mx-auto text-center text-themed leading-relaxed text-[15.5px] md:text-[16.5px]"
          >
            The Clarity Reset Session is designed to help you find the same kind of starting point in your own life, business, or creative direction.
          </p>
        </Reveal>

        {/* ─── One clear next action: see the proof. The Reset is a quiet
            secondary exit for visitors already primed by the bridge line. */}
        <Reveal delay={400}>
          <div className="mt-10 flex flex-col items-center gap-5">
            <a
              href="/proof"
              data-testid="showcase-teaser-map-cta"
              onClick={() => trackEvent("showcase_teaser_proof_cta_clicked")}
              className="btn-gold inline-flex items-center justify-center"
            >
              See the Proof
            </a>
            <button
              type="button"
              data-testid="showcase-teaser-reset-cta"
              onClick={openOrientationReset}
              className="text-[11px] tracking-[0.22em] uppercase text-gold/70 hover:text-gold transition-colors"
            >
              Or book the Clarity Reset Session · $149
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ShowcaseTeaser;
